import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { applyFroalaAriaFix, stripFroalaWatermark } from '../../../../common/utils/froala.utils';
import { Location } from "@angular/common";

import { Subscription, Subject } from 'rxjs';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import * as jp from "jsonpath/jsonpath";
import { debounceTime } from 'rxjs/operators';

import { FlowInstance, Question, QuestionKind } from '../../../../flow/models/flow.model';
import { BaseControlComponent } from '../../base-control/base-control.component';
import { ReportingService } from '../../../../common/services/reporting.service';
import { ContextInteractionService } from '../../../services/context-interaction.service';
import { ReportTemplate } from '../../../../models/comodel/mergefieldsmodel.model';

@Component({
    selector: 'app-report-template-editor',
    templateUrl: './report-template-editor.component.html',
    styleUrls: ['./report-template-editor.component.scss'],
    standalone: false
})
export class ReportTemplateEditorComponent extends BaseControlComponent implements OnInit {
    static componentName = 'ReportTemplateEditorComponent';

    public reportFlow: FlowInstance;
    public reportQuestionObservables: Array<{ [id: string]: { subject: Subject<string>, subscription: Subscription } }> = new Array<{ [id: string]: { subject: Subject<string>, subscription: Subscription } }>();

    isLoaded: boolean = false;
    questionKindEnum: any = QuestionKind;
    reportQuestions: Array<Array<Question>> = new Array<Array<Question>>();
    reportTarget: string;
    toolbarOptions: any;

    constructor(public readonly contextService: ContextInteractionService) {
        super(contextService);
        this.toolbarOptions = applyFroalaAriaFix({
            key: this.editorKey,
            heightMax: 500,
            heightMin: 200,
            toolbarButtons: ['fullscreen', 'html', '|', 'undo', 'redo', '|', 'bold', 'italic', 'underline', 'strikeThrough', '|', 'formatOL', 'formatUL', '|', 'align', 'fontSize', 'paragraphFormat', 'insertLink', '|', 'outdent', 'indent', 'clearFormatting', 'insertTable'],
        });
    }

    ngOnInit(): void {
        if (this.isEdit)
            return;
        const editor = document.getElementById("editor");
        if (editor) {
            if (this.enableEditing) {
                editor.setAttribute("contenteditable", "true");
            } else {
                editor.setAttribute("contenteditable", "false");
            }
        }
        this.setReportInstance();
    }

    onKeyUp(question: Question): void {
        this.reportQuestionObservables[question.key].subject.next(question.label);
    }

    public saveReportTemplate(question: Question): void {
        jp.value(this.componentContext, question.contextTarget, stripFroalaWatermark(question.label));
        this.flowComponent.workflow.saveContext();
    }

    public setReportInstance(): void {

        if (!this.flowComponent || !this.componentData.config.reportTarget) return;
        this.reportFlow = this.flowComponent.workflow;
        this.reportTarget = this.componentData.config.reportTarget;

        this.reportFlow.states.forEach(s => {
            s.sections.forEach(section => {
                section.pages.forEach(page => {
                    page.questions.forEach(questionGroup => {
                        questionGroup.forEach(question => {
                            if (question.contextTarget === this.reportTarget)
                                this.reportQuestions.push(questionGroup);
                        })
                    })
                })
            })
        })

        this.isLoaded = true;

        this.reportQuestions.forEach(questionGroup => {
            const question = questionGroup[0];

            // Don't keep bringing back the original label content, but use what's already in progress
            if (jp.value(this.componentContext, '$.reporting.hasEdited'))
                question.label = jp.value(this.componentContext, question.contextTarget);
            else
                this.replaceTokens(question);

            this.reportQuestionObservables[question.key] = { subject: undefined, subscription: undefined };
            this.reportQuestionObservables[question.key].subject = new Subject<string>().pipe(debounceTime(500));
            this.reportQuestionObservables[question.key].subscription = this.reportQuestionObservables[question.key].subject.subscribe(value => {
                this.saveReportTemplate(question);  // Save edits
            });
        });

        // Set a flag so we know not to use question.label as the source from now on, but use the stored value instead
        jp.value(this.componentContext, '$.reporting.hasEdited', true);
        this.flowComponent.workflow.saveContext();
    }

    public replaceTokens(question): void {
        // Find and replace report template fields with values stored in the context (i.e. Chief's Orders)
        if (this.componentContext.data.reportFlow) {
            const fields = (this.componentContext.data.reportFlow as ReportTemplate).reportContextValues;
            if (fields) {
                fields.forEach(x => {
                    const value = jp.value(this.componentContext, ReportingService.ReportContextPrefix + x.replaceValue);
                    if (value) question.label = (question.label.split(x.replaceValue)).join(value);
                });
            }
        }

        // Generic token replacement of all context.data values
        // that appear in the template label surrounded in brackets
        if (this.componentContext.data) {
            Object.keys(this.componentContext.data).forEach(key => {
                if (key !== 'reportTemplate') {

                    // Determine value
                    let val = this.componentContext.data[key];
                    let composedVal = '';
                    if (val instanceof Object && (val as Object).hasOwnProperty('value')) {
                        if (val.value instanceof Array) {
                            (val.value as string[]).forEach(element => {
                                composedVal += '<li>' + element + '</li>';
                            })
                        } else {
                            composedVal = val['value'];
                        }
                    } else {
                        composedVal = val;
                    }

                    // Surround full path with brackets
                    const token = '[$.data.' + key + ']';

                    // Useful to uncomment for debugging
                    //console.log(token + ': ' + composedVal);

                    // Replace label token with composedVal
                    question.label = (question.label.split(token)).join(composedVal);
                }
            })
        }

        // Currrent Date (special token)
        var currentDate = new Date().toLocaleDateString();
        question.label = question.label.split('[currentDate]').join(currentDate);

        this.saveReportTemplate(question);  // Initial save after find and replace
    }
}